import { useEffect, useMemo, useState } from "react";
import { fetchGt3xPublicCases } from "../../lib/publicCasesApi.js";
import CustomSelect from "../../components/CustomSelect.jsx";

function prettyError(err) {
    if (!err) return "Unbekannter Fehler";
    if (err instanceof Error) return err.message || "Fehler";
    return String(err);
}

function sourceLabel(sourceType) {
    if (sourceType === "driver_protest") return "Protest";
    return "Incident";
}

function statusTone(item) {
    if (item?.status === "published") return "published";
    return "review";
}

function StatusPill({ item }) {
    const tone = statusTone(item);

    const style = {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: ".04em",
        textTransform: "uppercase",
        border: "1px solid rgba(255,255,255,.12)",
        whiteSpace: "nowrap",
    };

    if (tone === "published") {
        style.color = "#8bd9a6";
        style.background = "rgba(139,217,166,.11)";
        style.borderColor = "rgba(139,217,166,.35)";
    } else {
        style.color = "#ffd470";
        style.background = "rgba(255,212,112,.10)";
        style.borderColor = "rgba(255,212,112,.30)";
    }

    return (
        <span style={style}>
            <span
                style={{
                    width: 7,
                    height: 7,
                    borderRadius: 999,
                    background: tone === "published" ? "#8bd9a6" : "#ffd470",
                    boxShadow:
                        tone === "published"
                            ? "0 0 10px rgba(139,217,166,.55)"
                            : "0 0 10px rgba(255,212,112,.55)",
                }}
            />
            {item?.public_status || "In Prüfung"}
        </span>
    );
}

function CaseCard({ item }) {
    const drivers = Array.isArray(item?.drivers) ? item.drivers : [];
    const final = item?.final_decision;
    const finalPenaltyRules = Array.isArray(final?.penalties)
        ? final.penalties
            .map((penalty) => penalty?.rule?.label)
            .filter(Boolean)
        : [];

    const displayedRuleLabel =
        finalPenaltyRules.length > 0
            ? [...new Set(finalPenaltyRules)].join(" / ")
            : item.initial_rule?.label || "Noch offen";
    const finalIsPenalty = final?.final_type === "penalty";
    const finalTone = finalIsPenalty
        ? {
            color: "#ff6b7a",
            background: "rgba(255,80,96,.10)",
            border: "1px solid rgba(255,80,96,.34)",
        }
        : {
            color: "#8bd9a6",
            background: "rgba(139,217,166,.08)",
            border: "1px solid rgba(139,217,166,.28)",
        };
    const penaltyPillStyle = {
        color: "#ff6b7a",
        background: "rgba(255,80,96,.12)",
        borderColor: "rgba(255,80,96,.38)",
    };

    const driverText = drivers.length
        ? drivers.map((d) => d.label || d.name || d.display_name || `Driver ${d.season_driver_id}`).join(" / ")
        : "Beteiligte Fahrer noch nicht hinterlegt";

    return (
        <article
            style={{
                position: "relative",
                overflow: "hidden",
                borderRadius: 18,
                padding: 18,
                background:
                    "linear-gradient(180deg, rgba(35,47,45,.96) 0%, rgba(22,31,30,.98) 100%)",
                border: "1px solid rgba(255,255,255,.08)",
                boxShadow: "0 14px 32px rgba(0,0,0,.28)",
            }}
        >
            <div
                style={{
                    position: "absolute",
                    inset: "0 auto 0 0",
                    width: 4,
                    background:
                        item?.status === "published"
                            ? "linear-gradient(180deg, #8bd9a6, rgba(139,217,166,.12))"
                            : "linear-gradient(180deg, var(--accent), rgba(228,254,60,.10))",
                }}
            />

            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                }}
            >
                <div>
                    <div
                        style={{
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                            alignItems: "center",
                            marginBottom: 8,
                        }}
                    >
                        <span
                            style={{
                                color: "var(--accent)",
                                fontWeight: 900,
                                fontSize: 22,
                                letterSpacing: ".04em",
                            }}
                        >
                            {item.case_number}
                        </span>

                        <span
                            style={{
                                fontSize: 12,
                                fontWeight: 800,
                                letterSpacing: ".08em",
                                textTransform: "uppercase",
                                color: "#9fd8ff",
                                background: "rgba(159,216,255,.10)",
                                border: "1px solid rgba(159,216,255,.30)",
                                padding: "4px 9px",
                                borderRadius: 999,
                            }}
                        >
                            {sourceLabel(item.source_type)}
                        </span>
                    </div>

                    <div
                        style={{
                            fontFamily: "var(--ff-Everett), sans-serif",
                            fontSize: 15,
                            color: "var(--text)",
                            lineHeight: 1.45,
                        }}
                    >
                        {driverText}
                    </div>
                </div>

                <StatusPill item={item} />
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 10,
                    marginTop: 16,
                }}
            >
                <div
                    style={{
                        padding: 12,
                        borderRadius: 14,
                        background: "rgba(255,255,255,.035)",
                        border: "1px solid rgba(255,255,255,.07)",
                    }}
                >
                    <div style={{ color: "var(--muted)", fontSize: 12, marginBottom: 4 }}>
                        Timestamp
                    </div>
                    <div style={{ fontWeight: 800 }}>
                        {item.incident_timestamp || "—"}
                    </div>
                </div>

                <div
                    style={{
                        padding: 12,
                        borderRadius: 14,
                        background: "rgba(255,255,255,.035)",
                        border: "1px solid rgba(255,255,255,.07)",
                    }}
                >
                    <div style={{ color: "var(--muted)", fontSize: 12, marginBottom: 4 }}>
                        Tatbestand
                    </div>
                    <div style={{ fontWeight: 800 }}>
                        {displayedRuleLabel}
                    </div>
                </div>
            </div>

            {final && (
                <div
                    style={{
                        marginTop: 16,
                        padding: 14,
                        borderRadius: 16,
                        background: finalTone.background,
                        border: finalTone.border,
                    }}
                >
                    <div
                        style={{
                            fontWeight: 900,
                            color: finalTone.color,
                            marginBottom: 8,
                        }}
                    >
                        Entscheidung: {final.final_type === "nfa" ? "NFA" : "Strafe"}
                    </div>

                    <div
                        style={{
                            fontFamily: "var(--ff-Everett), sans-serif",
                            color: "var(--text)",
                            lineHeight: 1.45,
                        }}
                    >
                        {final.final_reasoning}
                    </div>

                    {Array.isArray(final.penalties) && final.penalties.length > 0 && (
                        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
                            {final.penalties.map((p, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        display: "flex",
                                        gap: 10,
                                        flexWrap: "wrap",
                                        alignItems: "center",
                                        fontSize: 14,
                                    }}
                                >
                                    <span>
                                        <strong>{p.driver_name || "Fahrer"}</strong>
                                    </span>
                                    {p.penalty_seconds ? (
                                        <span className="pill" style={penaltyPillStyle}>
                                            {p.penalty_seconds}s
                                        </span>
                                    ) : null}
                                    {p.penalty_points ? (
                                        <span className="pill" style={penaltyPillStyle}>
                                            {p.penalty_points} SP
                                        </span>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </article>
    );
}

export default function Incidents({ seasonKey = "active" }) {
    const [view, setView] = useState("incidents");
    const [raceId, setRaceId] = useState("");
    const [races, setRaces] = useState([]);
    const [season, setSeason] = useState(null);
    const [cases, setCases] = useState([]);
    const [counts, setCounts] = useState({ total: 0, in_review: 0, published: 0 });
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const currentSeasonKey = String(seasonKey || "active").trim() || "active";

    const selectedRace = useMemo(() => {
        return races.find((r) => String(r.id) === String(raceId)) || null;
    }, [races, raceId]);

    async function loadData(nextRaceId = raceId, nextView = view) {
        setLoading(true);
        setErr("");

        try {
            const data = await fetchGt3xPublicCases({
                raceId: nextRaceId,
                caseType: nextView,
                seasonKey: currentSeasonKey,
            });

            setSeason(data.season || null);
            setRaces(Array.isArray(data.races) ? data.races : []);
            setCases(Array.isArray(data.cases) ? data.cases : []);
            setCounts(data.counts || { total: 0, in_review: 0, published: 0 });

            if (!nextRaceId && data.selected_race_id) {
                setRaceId(String(data.selected_race_id));
            }
        } catch (e) {
            setErr(prettyError(e));
            setCases([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        setRaceId("");
        loadData("", view);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSeasonKey]);

    useEffect(() => {
        if (!raceId) return;
        loadData(raceId, view);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [raceId, view, currentSeasonKey]);

    return (
        <div className="reko-modern-page">
            <section
                style={{
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 22,
                    padding: "24px clamp(18px, 3vw, 30px)",
                    background:
                        "radial-gradient(circle at top right, rgba(228,254,60,.14), transparent 34%), linear-gradient(135deg, rgba(35,47,45,.98), rgba(18,27,26,.98))",
                    border: "1px solid rgba(228,254,60,.18)",
                    boxShadow: "0 18px 42px rgba(0,0,0,.34)",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 18,
                        flexWrap: "wrap",
                        alignItems: "flex-end",
                    }}
                >
                    <div>
                        <div
                            style={{
                                color: "var(--accent)",
                                fontSize: 12,
                                fontWeight: 900,
                                letterSpacing: ".18em",
                                textTransform: "uppercase",
                                marginBottom: 8,
                            }}
                        >
                            RaceVision Incidents
                        </div>

                        <h1
                            style={{
                                margin: 0,
                                fontSize: "clamp(28px, 4vw, 48px)",
                                lineHeight: 1,
                                fontStyle: "italic",
                            }}
                        >
                            Rennkommission
                        </h1>

                        <p
                            className="muted"
                            style={{
                                marginTop: 10,
                                maxWidth: 760,
                                fontFamily: "var(--ff-Everett), sans-serif",
                            }}
                        >
                            Neue Datenbank-Ansicht für freigegebene ReKo-Fälle. Drafts bleiben verborgen,
                            freigegebene Fälle erscheinen als <strong>In Prüfung</strong>.
                        </p>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            gap: 10,
                            flexWrap: "wrap",
                        }}
                    >
                        <button
                            className={`button ${view === "incidents" ? "button--primary" : "button--secondary"}`}
                            type="button"
                            onClick={() => setView("incidents")}
                        >
                            Incidents
                        </button>

                        <button
                            className={`button ${view === "protests" ? "button--primary" : "button--secondary"}`}
                            type="button"
                            onClick={() => setView("protests")}
                        >
                            Proteste
                        </button>
                    </div>
                </div>
            </section>

            <section
                className="card"
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 14,
                    alignItems: "center",
                    flexWrap: "wrap",
                }}
            >
                <div>
                    <label
                        style={{
                            display: "block",
                            marginBottom: 6,
                            color: "var(--muted)",
                            fontSize: 13,
                        }}
                    >
                        Rennen auswählen
                    </label>

                    <CustomSelect
                        value={raceId}
                        onChange={setRaceId}
                        allowEmpty={false}
                        options={races.map((race) => ({
                            value: race.id,
                            label: race.label,
                        }))}
                    />
                </div>

                <div
                    style={{
                        display: "flex",
                        gap: 10,
                        flexWrap: "wrap",
                    }}
                >
                    <div className="pill pill--gesamt">
                        Gesamt: {counts.total || 0}
                    </div>
                    <div className="pill pill--int">
                        In Prüfung: {counts.in_review || 0}
                    </div>
                    <div className="pill pill--am">
                        Veröffentlicht: {counts.published || 0}
                    </div>
                </div>

                <div style={{ color: "var(--muted)", fontSize: 13 }}>
                    {season?.display_name ? (
                        <>
                            Saison: <strong>{season.display_name}</strong>
                        </>
                    ) : null}
                    {selectedRace?.label ? (
                        <>
                            {" "}
                            · Rennen: <strong>{selectedRace.label}</strong>
                        </>
                    ) : null}
                </div>
            </section>

            {err && (
                <div className="card">
                    <h2>Fehler</h2>
                    <p className="muted">{err}</p>
                </div>
            )}

            {loading && (
                <div className="card">
                    <p>Lade Cases…</p>
                </div>
            )}

            {!loading && !err && cases.length === 0 && (
                <div
                    className="card"
                    style={{
                        textAlign: "center",
                        padding: 28,
                    }}
                >
                    <h2>
                        {view === "incidents"
                            ? "Keine freigegebenen Incidents"
                            : "Keine Proteste vorhanden"}
                    </h2>

                    <p className="muted">
                        Für dieses Rennen sind aktuell keine Cases in der Datenbank freigegeben.
                    </p>
                </div>
            )}

            {!loading && !err && cases.length > 0 && (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 460px), 1fr))",
                        gap: 14,
                    }}
                >
                    {cases.map((item) => (
                        <CaseCard key={item.id} item={item} />
                    ))}
                </div>
            )}
        </div>
    );
}
